import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import Button from '../../../components/Button';
import { confirmDialog } from '../../../utils/confirm';
import { BookOpen, PlusCircle, Pencil, Trash2, Eye } from 'lucide-react';
import PageLoader from '../../../components/PageLoader';
import AddCourse from './AddCourse';
import ViewCourse from './ViewCourse';
import { getCourses as apiGetCourses, deleteCourse as apiDeleteCourse } from '../../../api';
import Swal from 'sweetalert2';

const AdminCourses = () => {
  const [courses, setCoursesState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [viewingCourse, setViewingCourse] = useState(null);

  const loadCourses = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError('');
    try {
      const list = await apiGetCourses();
      setCoursesState(list);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load courses');
      setCoursesState([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    loadCourses();
  }, []);

  const handleEdit = (c) => {
    setEditingCourse(c);
    setModalOpen(true);
  };

  const handleView = (c) => {
    setViewingCourse(c);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({ title: 'Delete course?', text: 'This course will be removed.' });
    if (!confirmed) return;
    try {
      await apiDeleteCourse(id);
      Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Course deleted successfully',
        confirmButtonColor: '#38915c',
      });
      await loadCourses(false);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete course';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: msg,
        confirmButtonColor: '#38915c',
      });
      setError(msg);
    }
    if (editingCourse?.id === id) {
      setEditingCourse(null);
      setModalOpen(false);
    }
  };

  const openAddModal = () => {
    setEditingCourse(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingCourse(null);
  };

  const closeDetailsModal = () => {
    setViewingCourse(null);
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-primary-600 mb-1">
            <BookOpen className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wide">Manage</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Courses</h1>
          <p className="text-gray-500 text-sm mt-0.5">Add and edit courses</p>
        </div>
        <Button variant="primary" size="sm" onClick={openAddModal} className="flex items-center gap-2">
          <PlusCircle className="w-4 h-4" />
          Add Course
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm">
          {error}
        </div>
      )}

      {loading && <PageLoader />}

      <AddCourse
        open={modalOpen}
        onClose={closeModal}
        onSuccess={() => loadCourses(false)}
        editCourse={editingCourse}
      />

      <ViewCourse
        open={Boolean(viewingCourse)}
        onClose={closeDetailsModal}
        course={viewingCourse}
      />

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Course name</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Basic video</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Lessons</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Duration</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Growth plan</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Price</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {courses.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-gray-500">
                    No courses yet. Click "Add Course" to create one.
                  </td>
                </tr>
              ) : (
                courses.map((c) => (
                  <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{c.title}</td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {c.basicVideoUrl ? (
                        <a href={c.basicVideoUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline truncate block max-w-[180px]">
                          Video
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-gray-600">{(c.lessons || []).length} lesson(s)</span>
                    </td>
                    <td className="py-3 px-4">{c.duration || '—'}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                        {c.courseType === 'crypto' ? 'Crypto' : 'Indian'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700">
                      {c.underGrowthPlan ? 'Yes' : 'No'}
                    </td>
                    <td className="py-3 px-4">{c.price ? `₹${c.price}` : '—'}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Tooltip title="View Details">
                          <button
                            type="button"
                            onClick={() => handleView(c)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-secondary-600 hover:bg-secondary-50 hover:text-secondary-700 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip title="Lessons">
                          <Link
                            to={`/admin/courses/${c.id}`}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                          >
                            <BookOpen className="w-4 h-4" />
                          </Link>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <button
                            type="button"
                            onClick={() => handleEdit(c)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <button
                            type="button"
                            onClick={() => handleDelete(c.id)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-danger-600 hover:bg-danger-50 hover:text-danger-700 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminCourses;
